import { useAuthStore } from "../stores/authStore";
import { ErrorMessages } from "./i18nUtils";

/**
 * Utility functions for authentication and token management
 */

let tokenExpirationNotified = false; // Prevent multiple notifications

/**
 * Handles token expiration (401 errors) with user-friendly notifications and redirection
 * NOTE: This should ONLY be called for 401 Unauthorized errors (token expired/invalid)
 * Do NOT call this for 403 Forbidden errors (insufficient permissions)
 * @param showNotification - Function to show notification to user
 * @param redirectPath - Path to redirect to (default: /auth/login)
 */
export const handleTokenExpiration = (
  showNotification?: (message: string, type: "warning" | "error") => void,
  redirectPath: string = "/auth/login"
) => {
  // Prevent multiple notifications for the same expiration event
  if (tokenExpirationNotified) {
    return;
  }

  tokenExpirationNotified = true;

  console.warn(
    "Token expired or unauthorized access detected. Logging out user."
  );

  // Clear authentication state
  const { logout } = useAuthStore.getState();
  logout();

  // Show notification if available
  if (showNotification) {
    // Try to use the special session expired method if available
    if (
      globalNotificationHandler &&
      globalNotificationHandler.showSessionExpired
    ) {
      globalNotificationHandler.showSessionExpired();
    } else {
      showNotification(
        ErrorMessages.sessionExpired(),
        "warning"
      );
    }
  }

  // Redirect after a short delay to ensure state updates complete
  setTimeout(() => {
    tokenExpirationNotified = false; // Reset for future sessions
    window.location.href = redirectPath;
  }, 1500); // Slightly longer delay to show notification
};

// Global notification handler reference
let globalNotificationHandler: any = null;

/**
 * Sets the global notification handler (called by NotificationProvider)
 */
export const setGlobalNotificationHandler = (handler: any) => {
  globalNotificationHandler = handler;
};

/**
 * Creates a notification function compatible with different notification systems
 * This function can be used with react-notification-alert or other systems
 */
export const createNotificationHandler = () => {
  return (message: string, type: "warning" | "error" | "info" | "success") => {
    // Try to use global notification handler first
    if (globalNotificationHandler) {
      switch (type) {
        case "success":
          globalNotificationHandler.showSuccess(message);
          break;
        case "warning":
          globalNotificationHandler.showWarning(message);
          break;
        case "error":
          globalNotificationHandler.showError(message);
          break;
        case "info":
        default:
          globalNotificationHandler.showInfo(message);
          break;
      }
      return;
    }

    // Fallback to console and browser alert
    console.log(`[${type.toUpperCase()}] ${message}`);
    if (type === "warning" || type === "error") {
      alert(message);
    }
  };
};

/**
 * Checks if an endpoint is an authentication endpoint to avoid redirect loops
 */
export const isAuthEndpoint = (endpoint: string): boolean => {
  const authEndpoints = [
    "/auth/",
    "/login",
    "/logout",  // Explicitly exclude logout from 401 handling to avoid loops
    "/check_phone",
    "/register",
    "/reset-password",
    "/verify",
  ];

  return authEndpoints.some((authEndpoint) => endpoint.includes(authEndpoint));
};

/**
 * Handles permission errors (403 Forbidden) without logging out the user
 * @param showNotification - Function to show notification to user
 * @param customMessage - Optional custom error message
 */
export const handlePermissionError = (
  showNotification?: (message: string, type: "error") => void,
  customMessage?: string
) => {
  const message = customMessage || ErrorMessages.forbidden();

  console.warn("Permission denied (403). User remains logged in.");

  // Show notification if available
  if (showNotification) {
    showNotification(message, "error");
  }
};

/**
 * Enhanced error message parser for authentication errors
 * NOTE: This should primarily be used for 401 errors. For 403 errors, use handlePermissionError instead.
 */
export const parseAuthError = (error: any, statusCode: number): string => {
  // Handle token expiration specifically (401)
  if (statusCode === 401) {
    if (error?.message && error.message.includes("expired")) {
      return ErrorMessages.sessionExpired();
    }
    if (error?.message && error.message.includes("token")) {
      return ErrorMessages.sessionExpired();
    }
    return ErrorMessages.sessionExpired();
  }

  // Handle permission errors (403) - though these should use handlePermissionError instead
  if (statusCode === 403) {
    return ErrorMessages.forbidden();
  }

  // Parse other authentication errors
  if (error?.message) {
    if (error.message.includes("token")) {
      return ErrorMessages.sessionExpired();
    }
    if (error.message.includes("expired")) {
      return ErrorMessages.sessionExpired();
    }
    if (error.message.includes("unauthorized")) {
      return ErrorMessages.unauthorized();
    }
  }

  return ErrorMessages.sessionExpired();
};

export default {
  handleTokenExpiration,
  handlePermissionError,
  createNotificationHandler,
  setGlobalNotificationHandler,
  isAuthEndpoint,
  parseAuthError,
};
